import { useState } from "react";
import { toast } from "sonner";
import { Truck, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CARRIER_NAMES, orderMode } from "./orderMeta";
import { AddressBlock } from "./AddressForm";
import { OrderTimeline, ShipmentHistory } from "./OrderTimeline";
import { TrackingLink } from "./BuyerOrderPanel";

export function ShipmentForm({ o, reload, admin = false, onSaved }) {
  const sh = o.shipment || {};
  const [f, setF] = useState({ carrier: sh.carrier || "royal_mail", carrier_name: sh.carrier === "other" ? sh.carrier_name || "" : "", tracking_number: sh.tracking_number || "", tracking_url: sh.carrier === "other" ? sh.tracking_url || "" : "", note: sh.note || "", reason: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const noTracking = f.carrier === "own_delivery";
  const missingAddr = o.shipping_address_status !== "collected" && !(o.shipping_address?.line1);

  const save = async (thenShip) => {
    setBusy(true);
    try {
      await api.put(`/orders/${o.id}/shipment`, f);
      if (thenShip) { await api.post(`/orders/${o.id}/status`, { status: "shipped" }); toast.success("Encomenda marcada como enviada"); }
      else toast.success(o.status === "shipped" ? "Rastreio atualizado — cliente notificado" : "Dados de envio guardados");
      reload?.(); onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const inp = "w-full px-3 py-2 rounded-lg border border-zinc-200 text-xs bg-white";
  return (
    <div data-testid={`shipment-form-${o.order_number}`} className="mt-2 p-3 rounded-xl border border-zinc-200 bg-zinc-50 space-y-2">
      <div className="text-xs font-semibold flex items-center gap-1"><Truck size={12} /> Envio</div>
      <select data-testid={`carrier-select-${o.order_number}`} className={inp} value={f.carrier} onChange={upd("carrier")}>
        {Object.entries(CARRIER_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {f.carrier === "other" && <input data-testid={`carrier-name-${o.order_number}`} className={inp} placeholder="Nome da transportadora" value={f.carrier_name} onChange={upd("carrier_name")} />}
      {!noTracking && <input data-testid={`tracking-input-${o.order_number}`} className={`${inp} font-mono`} placeholder="Número de rastreio (opcional)" value={f.tracking_number} onChange={upd("tracking_number")} />}
      {f.carrier === "other" && <input data-testid={`tracking-url-${o.order_number}`} className={inp} placeholder="Link de rastreio https:// (opcional)" value={f.tracking_url} onChange={upd("tracking_url")} />}
      {noTracking && <p className="text-[11px] text-zinc-500">Entrega feita por ti — sem número de rastreio.</p>}
      <input className={inp} placeholder="Nota para o cliente (opcional)" value={f.note} onChange={upd("note")} />
      {admin && <input data-testid={`shipment-reason-${o.order_number}`} className={inp} placeholder="Motivo da correção (obrigatório)" value={f.reason} onChange={upd("reason")} />}
      {!f.tracking_number && !noTracking && <p className="text-[11px] text-orange-700">Sem número de rastreio o cliente não poderá acompanhar a encomenda.</p>}
      {missingAddr && <p data-testid={`seller-address-missing-${o.order_number}`} className="text-[11px] text-red-700 font-semibold">Morada de entrega em falta — o cliente foi notificado. Não é possível marcar como enviada.</p>}
      <div className="flex gap-2">
        <button data-testid={`shipment-save-${o.order_number}`} disabled={busy} onClick={() => save(false)} className="flex-1 py-2 rounded-lg border border-black text-xs font-bold disabled:opacity-50">Guardar</button>
        {!admin && o.status !== "shipped" && <button data-testid={`ship-now-${o.order_number}`} disabled={busy || missingAddr} onClick={() => save(true)} className="flex-1 py-2 rounded-lg bg-black text-white text-xs font-bold disabled:opacity-50">Marcar como enviada</button>}
      </div>
    </div>
  );
}

export function SellerDeliveryPanel({ o, reload }) {
  const [open, setOpen] = useState(false);
  const mode = orderMode(o);
  const active = !["pending", "cancelled", "refunded", "completed"].includes(o.status);
  return (
    <div className="mt-2 pt-2 border-t border-zinc-100 space-y-2">
      <div className="flex items-center justify-between text-[11px]">
        <span className="font-semibold">{mode === "delivery" ? "Entrega" : "Levantamento na loja"}</span>
        {o.confirmed_by && <span data-testid={`seller-confirmed-${o.order_number}`} className="text-zinc-500">Confirmado por {o.confirmed_by === "buyer" ? "cliente" : "loja"}</span>}
      </div>
      <OrderTimeline o={o} />
      {mode === "delivery" && <div className="flex items-start gap-1"><MapPin size={12} className="mt-0.5 shrink-0 text-zinc-400" /><AddressBlock o={o} testid={`seller-address-${o.order_number}`} /></div>}
      {mode === "delivery" && o.shipment?.carrier && !open && <TrackingLink o={o} testid={`seller-tracking-${o.order_number}`} />}
      {mode === "delivery" && active && (
        open ? <ShipmentForm o={o} reload={reload} onSaved={() => setOpen(false)} />
             : <button data-testid={`shipment-open-${o.order_number}`} onClick={() => setOpen(true)} className="text-[11px] font-semibold px-2 py-1.5 rounded-lg bg-zinc-800 text-white hover:bg-black flex items-center gap-1"><Truck size={11} /> {o.shipment?.carrier ? "Editar envio / rastreio" : "Registar envio"}</button>
      )}
      <ShipmentHistory o={o} testid={`seller-shipment-history-${o.order_number}`} />
    </div>
  );
}
